import { Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { HMSRoomProvider } from "@100mslive/react-sdk";
import { FullPageSpinner } from "@/components/common/FullPageSpinner";
import { IncompleteLink, NotFound } from "@/components/common/RouteErrors";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Conference from "@/components/Conference";
import { Notifications } from "@/components/meeting/Notifications";
import PostLeave from "@/components/PostLeave";
import PreviewScreen from "@/components/PreviewScreen";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

/** `/{roomId}/{role}` is the link the platform emails. */
const RedirectToPreview = () => {
  const { roomId, role } = useParams();
  return <Navigate replace to={`/preview/${roomId}/${role}`} />;
};

// No route is code-split: after a deploy the old chunk files are gone, and
// someone still on the old preview who presses Join would fail to load the
// call.
export const AppRoutes = () => (
  <Suspense fallback={<FullPageSpinner />}>
    <Routes>
      <Route path="/preview/:roomId/:role" element={<PreviewScreen />} />
      <Route path="/meeting/:roomId/:role" element={<Conference />} />
      <Route path="/leave/:roomId/:role" element={<PostLeave />} />
      <Route path="/preview/:roomId" element={<IncompleteLink />} />
      <Route path="/meeting/:roomId" element={<IncompleteLink />} />
      <Route path="/leave/:roomId" element={<IncompleteLink />} />
      <Route path="/:roomId/:role" element={<RedirectToPreview />} />
      <Route path="/:roomId" element={<IncompleteLink />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    <Notifications />
  </Suspense>
);

// Above the call's control bar, which sets --control-bar-height; phones get the
// same offset, or toasts cover Leave.
const TOAST_OFFSET = {
  bottom: "calc(var(--control-bar-height, 0px) + 16px)",
  left: 16,
};

export default function App() {
  return (
    <ErrorBoundary>
      <HMSRoomProvider>
        <TooltipProvider delayDuration={300}>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster
            position="bottom-left"
            offset={TOAST_OFFSET}
            mobileOffset={TOAST_OFFSET}
          />
        </TooltipProvider>
      </HMSRoomProvider>
    </ErrorBoundary>
  );
}
